import { Fragment, useState, type FormEvent, type ReactNode } from 'react';
import { fetchNotificationPreferences, saveNotificationPreferences } from '../api/endpoints';
import { toErrorMessage } from '../api/client';
import type { NotificationPreferences, NotificationRecipientMode, NotificationSendTo } from '../api/types';
import { useApiData } from '../hooks/useApiData';
import { ErrorState, LoadingState } from './LoadingState';
import { useToast } from './ToastProvider';

/** The on/off toggles only (not the "Send to" settings or the recipient list). */
type PrefKey = {
  [K in keyof NotificationPreferences]: NotificationPreferences[K] extends boolean ? K : never;
}[keyof NotificationPreferences];

/** The groups with a "Send to" choice; Support has none (it replies to the person who asked). */
type SendToKey = 'accountActivitySendTo' | 'prepaidCreditSendTo' | 'apiKeysSendTo';

interface PrefDefinition {
  key: PrefKey;
  title: string;
  description: string;
}

/*
 * Only emails the platform actually sends. Same list, wording and groups as the Razor partial
 * web/Pages/Shared/_NotificationPreferences.cshtml; keep the two the same. Wording is neutral ("the
 * balance", not "your balance") because a SuperAdmin reads it on Client detail too.
 */
const ACTIVITY_PREFS: PrefDefinition[] = [
  {
    key: 'accountLocked',
    title: 'Account locked',
    description:
      'A user was locked out after 5 wrong passwords within 15 minutes. The user is always told; this tells the admins too.',
  },
  {
    key: 'accountStatusChanged',
    title: 'Account status changed',
    description: 'The account was suspended, deactivated or reactivated, or Beta Live ended.',
  },
  {
    key: 'planChanged',
    title: 'New pricing plan',
    description: 'The account went live on a pricing plan, moved plan, or its Custom terms changed.',
  },
  {
    key: 'liveAccessUpdates',
    title: 'Live access request',
    description: 'The request for live access was received, approved (Beta Live starts) or rejected, with the reason.',
  },
];

const CREDIT_PREFS: PrefDefinition[] = [
  { key: 'creditsLow', title: 'Credit running low', description: 'The prepaid balance is getting close to empty.' },
  {
    key: 'creditsExhausted',
    title: 'Credit exhausted',
    description: 'Credit has run out and verifications are blocked until a top-up.',
  },
  {
    key: 'creditPurchased',
    title: 'Credit purchased',
    description: 'A purchase or automatic top-up added credit to the balance.',
  },
  { key: 'paymentFailed', title: 'Credit failed', description: 'A credit purchase was declined, so no credit was added.' },
  {
    key: 'autoTopUpFailed',
    title: 'Auto top-up failed',
    description: 'An automatic top-up was declined: the card needs attention.',
  },
];

const API_KEY_PREFS: PrefDefinition[] = [
  { key: 'apiKeyAdded', title: 'API key created', description: 'A new API key was issued for the account.' },
  {
    key: 'apiKeyRotatedOrRevoked',
    title: 'API key rotated or revoked',
    description: 'An existing key was given a new secret, or switched off.',
  },
];

const SUPPORT_PREFS: PrefDefinition[] = [
  {
    key: 'supportRepliesToAdmins',
    title: 'Support replies to admins',
    description:
      "Copy every company admin on the team's replies to support requests. Off by default: the person who raised a request is always emailed.",
  },
];

/**
 * Which notification emails a client receives. Account → Notifications passes no clientId (the
 * signed-in client's own; read-only unless ClientAdmin, which the server enforces too); the
 * Notifications tab on Client detail passes one (SuperAdmin).
 */
export function NotificationPreferencesForm({
  clientId,
  readOnly = false,
}: {
  clientId?: number;
  readOnly?: boolean;
}) {
  const { data, loading, error } = useApiData(() => fetchNotificationPreferences(clientId), [clientId]);

  if (loading) return <LoadingState label="Loading preferences" />;
  if (error) return <ErrorState message={error} />;
  if (!data) return <ErrorState message="Preferences could not be loaded." />;

  return <Form clientId={clientId} initial={data} readOnly={readOnly} />;
}

function Form({
  clientId,
  initial,
  readOnly,
}: {
  clientId?: number;
  initial: NotificationPreferences;
  readOnly: boolean;
}) {
  const { showSuccess, showError } = useToast();
  const [prefs, setPrefs] = useState<NotificationPreferences>(initial);
  const [saving, setSaving] = useState(false);

  function toggle(key: PrefKey) {
    setPrefs((current) => ({ ...current, [key]: !current[key] }));
  }

  /** The bar under a group's toggles; read-only screens show it disabled through the fieldset. */
  function sendTo(key: SendToKey) {
    return (
      <SendToBar
        id={key}
        value={prefs[key]}
        company={prefs.companyRecipients}
        onChange={(value) => setPrefs((current) => ({ ...current, [key]: value }))}
      />
    );
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    try {
      const saved = await saveNotificationPreferences(prefs, clientId);
      setPrefs(saved);
      showSuccess('Notification preferences saved.');
    } catch (err) {
      // A refused "Send to" comes back as a 400 naming the group and what to fix.
      showError(toErrorMessage(err, 'An error occurred while saving. Please try again.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      {/* A disabled fieldset turns every switch inside read-only in one place. */}
      <fieldset disabled={readOnly} className="m-0 p-0 border-0" style={{ minWidth: 0 }}>
        <div className="row g-4">
          <div className="col-lg-6">
            <PrefCard
              title="Account Activity"
              icon="bi-person-gear text-primary"
              definitions={ACTIVITY_PREFS}
              prefs={prefs}
              onToggle={toggle}
              leading={<SignupSwitch />}
              footer={sendTo('accountActivitySendTo')}
            >
              <div className="form-text mt-3">
                <i className="bi bi-info-circle me-1" />
                Also always sent, whatever is set here: sign-in codes, and password and email-verification
                emails.
              </div>
            </PrefCard>
          </div>
          <div className="col-lg-6">
            <PrefCard
              title="Prepaid Credit"
              icon="bi-wallet2 text-primary"
              definitions={CREDIT_PREFS}
              prefs={prefs}
              onToggle={toggle}
              footer={sendTo('prepaidCreditSendTo')}
            />
          </div>
          <div className="col-lg-6">
            <PrefCard
              title="API Keys"
              icon="bi-key text-success"
              definitions={API_KEY_PREFS}
              prefs={prefs}
              onToggle={toggle}
              footer={sendTo('apiKeysSendTo')}
            />
          </div>
          <div className="col-lg-6">
            <PrefCard title="Support" icon="bi-headset text-primary" definitions={SUPPORT_PREFS} prefs={prefs} onToggle={toggle} />
          </div>
        </div>
      </fieldset>

      {readOnly ? (
        <p className="small text-body-secondary mt-4 mb-0">
          <i className="bi bi-lock me-1" />
          Only a company admin can change these preferences.
        </p>
      ) : (
        <div className="mt-4">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            <i className="bi bi-floppy me-1" />
            {saving ? 'Saving…' : 'Save preferences'}
          </button>
        </div>
      )}
    </form>
  );
}

/**
 * Signup: shown, but always on and not a preference. The welcome email carries the new user's
 * temporary password and goes out when the account is created, before anyone could open these
 * settings. Not part of NotificationPreferences, so nothing is sent to or stored by the API.
 */
function SignupSwitch() {
  return (
    <div className="col-12">
      <div className="form-check form-switch">
        <input id="pref-signup" className="form-check-input" type="checkbox" role="switch" checked disabled readOnly />
        <label className="form-check-label fw-semibold" htmlFor="pref-signup">
          Signup
        </label>
      </div>
      <div className="form-text">
        Welcome email with the temporary password when the account is created. Always on: it is how a new user signs in.
      </div>
    </div>
  );
}

function PrefCard({
  title,
  icon,
  definitions,
  prefs,
  onToggle,
  leading,
  children,
  footer,
}: {
  title: string;
  icon: string;
  definitions: PrefDefinition[];
  prefs: NotificationPreferences;
  onToggle: (key: PrefKey) => void;
  /** Shown before the toggles, e.g. the always-on Signup switch. */
  leading?: ReactNode;
  /** Shown under the toggles, e.g. the Account Activity "always sent" note. */
  children?: ReactNode;
  /** Below the card body, e.g. the group's "Send to" bar. */
  footer?: ReactNode;
}) {
  return (
    <div className="card h-100">
      <div className="card-header">
        <h6 className="mb-0 fw-semibold">
          <i className={`bi ${icon} me-2`} />
          {title}
        </h6>
      </div>
      <div className="card-body">
        <div className="row g-3">
          {leading}
          {definitions.map((def) => (
            // Same shape as the toggles in System Settings: a semibold label, the description as form-text.
            <div className="col-12" key={def.key}>
              <div className="form-check form-switch">
                <input
                  id={`pref-${def.key}`}
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  checked={prefs[def.key]}
                  onChange={() => onToggle(def.key)}
                />
                <label className="form-check-label fw-semibold" htmlFor={`pref-${def.key}`}>
                  {def.title}
                </label>
              </div>
              <div className="form-text">{def.description}</div>
            </div>
          ))}
        </div>
        {children}
      </div>
      {footer}
    </div>
  );
}

// ── "Send to" ─────────────────────────────────────────────────────────────────

/** Same limit as Core NotificationRecipientRules.MaxAddresses; the server checks it again. */
const MAX_ADDRESSES = 5;

/** A first check before Add; the server's rule (EmailAddressAttribute, a dot) is the authority. */
const ADDRESS_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const MODES: { value: NotificationRecipientMode; label: string }[] = [
  { value: 'allAdmins', label: 'All admins' },
  { value: 'adminsAndAddresses', label: 'Admins + addresses' },
  { value: 'addressesOnly', label: 'Only these addresses' },
];

/**
 * Who receives one group (since 2026-10-25): all admins (the company contact and every active
 * Client Admin, listed), those plus the group's own addresses, or only its own addresses. Up to 5
 * addresses of any domain; "Only these addresses" needs at least one, which the server enforces.
 */
function SendToBar({
  id,
  value,
  company,
  onChange,
}: {
  id: string;
  value: NotificationSendTo;
  company: string[];
  onChange: (value: NotificationSendTo) => void;
}) {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const admins = Math.max(company.length - 1, 0);
  const who = `${admins} admin${admins === 1 ? '' : 's'} and the company contact`;

  function add() {
    const address = draft.trim().toLowerCase();
    if (!address) return;
    if (!ADDRESS_PATTERN.test(address)) return setError(`"${draft.trim()}" is not a valid email address.`);
    if (value.addresses.includes(address)) return setError('That address is already on the list.');
    if (value.addresses.length >= MAX_ADDRESSES) return setError(`Add at most ${MAX_ADDRESSES} addresses.`);
    onChange({ ...value, addresses: [...value.addresses, address] });
    setDraft('');
    setError(null);
  }

  return (
    <div className="card-footer notification-send-to">
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
        <span className="fw-semibold small">
          <i className="bi bi-envelope me-1" />
          Send to
        </span>
        <div className="btn-group btn-group-sm" role="radiogroup" aria-label="Send to">
          {MODES.map((m) => (
            <Fragment key={m.value}>
              <input
                type="radio"
                className="btn-check"
                name={`${id}-mode`}
                id={`${id}-${m.value}`}
                checked={value.mode === m.value}
                onChange={() => onChange({ ...value, mode: m.value })}
              />
              <label className="btn btn-outline-secondary" htmlFor={`${id}-${m.value}`}>
                {m.label}
              </label>
            </Fragment>
          ))}
        </div>
      </div>

      {value.mode === 'allAdmins' && (
        <div className="small text-body-secondary">
          <i className="bi bi-people me-1" />
          {who}: {company.join(', ')}
        </div>
      )}
      {value.mode === 'adminsAndAddresses' && (
        <div className="small text-body-secondary mb-2">
          <i className="bi bi-people me-1" />
          {who}, plus:
        </div>
      )}
      {value.mode === 'addressesOnly' && (
        <div className="small text-body-secondary mb-2">
          <i className="bi bi-person-x me-1" />
          Admins and the company contact won't receive these:
        </div>
      )}

      {value.mode !== 'allAdmins' && (
        <>
          {value.addresses.length > 0 && (
            <div className="mb-1">
              {value.addresses.map((address) => (
                <span key={address} className="send-to-chip">
                  {address}
                  <button
                    type="button"
                    className="btn btn-link p-0 border-0"
                    aria-label={`Remove ${address}`}
                    onClick={() => onChange({ ...value, addresses: value.addresses.filter((a) => a !== address) })}
                  >
                    <i className="bi bi-x" />
                  </button>
                </span>
              ))}
            </div>
          )}
          <div className="input-group input-group-sm send-to-add">
            <input
              type="email"
              className={`form-control ${error ? 'is-invalid' : ''}`}
              placeholder="name@company.com"
              aria-label="Email address to add"
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value);
                setError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  add();
                }
              }}
            />
            <button type="button" className="btn btn-outline-secondary" onClick={add}>
              <i className="bi bi-plus-lg me-1" />
              Add
            </button>
          </div>
          {error ? (
            <div className="small text-danger mt-1">{error}</div>
          ) : (
            <div className="form-text">
              {value.mode === 'addressesOnly' ? 'At least one address. ' : ''}Up to {MAX_ADDRESSES} addresses.
            </div>
          )}
        </>
      )}
    </div>
  );
}
